import React, { useState, useMemo, useRef, useEffect } from 'react';
import { inputBaseClass, getInitials } from '../../utils/formatters';

/**
 * CompanySuggestions - Input with dropdown showing similar companies
 * Used to prevent duplicate company creation
 *
 * Props:
 * - value: string - Current input value
 * - companies: Company[] - List of all companies to search
 * - onSelect: (company) => void - Called when user selects existing company
 * - onChange: (value) => void - Called when input changes
 * - selectedCompany: Company | null - Currently selected existing company
 * - placeholder: string
 * - error: string
 * - label: string
 * - required: boolean
 * - disabled: boolean
 * - excludeId: string - Company ID to exclude (when editing)
 */

const CompanySuggestions = ({
  value,
  companies = [],
  onSelect,
  onChange,
  selectedCompany = null,
  placeholder = 'Enter company name...',
  error,
  label = 'Company Name',
  required = false,
  disabled = false,
  excludeId = null,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter companies by "contains" matching (case-insensitive)
  const suggestions = useMemo(() => {
    if (!value || value.length < 2 || selectedCompany) return [];

    const searchTerm = value.toLowerCase().trim();
    return companies
      .filter(company => {
        // Exclude company being edited
        if (excludeId && company.id === excludeId) return false;
        // Match by name or displayName
        return (
          company.name?.toLowerCase().includes(searchTerm) ||
          company.displayName?.toLowerCase().includes(searchTerm)
        );
      })
      .slice(0, 5); // Limit to 5 suggestions
  }, [value, companies, selectedCompany, excludeId]);

  // Show dropdown when we have suggestions and input is focused
  const showDropdown = isFocused && suggestions.length > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    // Clear selection when user types
    if (selectedCompany) {
      onSelect(null);
    }
  };

  const handleSelect = (company) => {
    onSelect(company);
    onChange(company.name);
    setIsFocused(false);
  };

  const handleClearSelection = () => {
    onSelect(null);
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      {/* Label */}
      {label && (
        <label
          htmlFor="company-name-input"
          className="block text-sm font-medium text-slate-700 dark:text-[#a3bfdb] mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input container */}
      <div className="relative">
        <input
          ref={inputRef}
          id="company-name-input"
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          placeholder={placeholder}
          className={`${inputBaseClass} ${error ? 'border-red-500' : ''} ${
            selectedCompany ? 'pr-10' : ''
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />

        {/* Clear button when company is selected */}
        {selectedCompany && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
            title="Clear selection"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Error message */}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {/* Selected company badge */}
      {selectedCompany && (
        <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700">
          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-blue-700 dark:text-blue-300">
            Linked to existing company
          </span>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] rounded-lg shadow-lg max-h-72 overflow-y-auto"
        >
          {/* Warning header */}
          <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-xs font-medium">Similar companies found</span>
            </div>
          </div>

          {/* Suggestions list */}
          {suggestions.map((company) => (
            <button
              key={company.id}
              type="button"
              onClick={() => handleSelect(company)}
              className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-[#233648] transition-colors text-left border-b border-gray-100 dark:border-[#233648] last:border-b-0"
            >
              {company.logoUrl ? (
                <img
                  src={company.logoUrl}
                  alt={company.name}
                  className="w-8 h-8 rounded-lg object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-semibold">
                  {getInitials(company.name)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {company.name}
                </p>
                {company.industry && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {company.industry}
                  </p>
                )}
              </div>
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Select</span>
            </button>
          ))}

          {/* Create new option */}
          <button
            type="button"
            onClick={() => setIsFocused(false)}
            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors text-left border-t border-gray-200 dark:border-[#324d67]"
          >
            <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#233648] flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Create "{value}"
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Add as new company
              </p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default CompanySuggestions;
